import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LoginInput, PublicUser } from '@hxiq/shared';
import { useQueryClient } from '@tanstack/react-query';
import { api, platformApi, setAuthLostHandler } from './api';
import { impersonation } from './token-store';

/** Where the console lives; a shop session opened from it returns here. */
const PLATFORM_HOME = '/platform/tenants';

/**
 * Set once a reload inside a shop entered from the console has been sent
 * back to the console. Module-level so StrictMode's second effect run does
 * not start a restore, which would end in ProtectedRoute bouncing to the
 * login screen a moment before the navigation lands.
 */
let returningToConsole = false;

interface AuthContextValue {
  user: PublicUser | null;
  isLoading: boolean;
  /**
   * True when the server ended the session out from under the person — the
   * refresh token expired or was revoked — as opposed to them logging out.
   * The login screen says so; a deliberate log out must not.
   */
  sessionLost: boolean;
  login: (input: LoginInput) => Promise<void>;
  startDemo: () => Promise<void>;
  logout: () => Promise<void>;
  /** From the platform console: open a short shop session as support. */
  enterTenant: (tenantId: string) => Promise<PublicUser>;
  /** Back to the console; the shop session is simply dropped. */
  leaveTenant: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionLost, setSessionLost] = useState(false);

  // Query keys carry no tenant or user, so the cache belongs to whoever was
  // signed in when it filled. It is dropped at every session boundary: on a
  // shared shop workstation the next person to log in must never see the last
  // person's jobs — not even for the second before a refetch lands.
  const queryClient = useQueryClient();

  useEffect(() => {
    setAuthLostHandler((reason) => {
      queryClient.clear();
      // Both in one batch, so the render that drops the user already knows
      // why: ProtectedRoute reads the flag as it sends the person to log in.
      setSessionLost(reason === 'session');
      setUser(null);
      // The console is outside the router's authenticated tree, so a plain
      // navigation is the honest way back to it.
      if (reason === 'impersonation') window.location.assign(PLATFORM_HOME);
    });
    // A reload while inside a shop entered from the console: the session is
    // gone (it had no refresh token) and the console is where to land. The
    // provider stays "loading" so nothing else decides where to go first.
    if (impersonation.active() || returningToConsole) {
      returningToConsole = true;
      impersonation.set(false);
      if (!window.location.pathname.startsWith('/platform')) window.location.replace(PLATFORM_HOME);
      return;
    }
    // The access token is memory-only, so a reload always starts here.
    void api.restore().then((restored) => {
      setUser(restored);
      setIsLoading(false);
    });
  }, [queryClient]);

  const login = useCallback(async (input: LoginInput) => {
    const { user: loggedIn } = await api.login(input);
    queryClient.clear();
    setSessionLost(false);
    setUser(loggedIn);
  }, [queryClient]);

  const startDemo = useCallback(async () => {
    const { user: demoUser } = await api.startDemo();
    queryClient.clear();
    setSessionLost(false);
    setUser(demoUser);
  }, [queryClient]);

  const logout = useCallback(async () => {
    await api.logout();
    queryClient.clear();
    setSessionLost(false);
    setUser(null);
  }, [queryClient]);

  const enterTenant = useCallback(async (tenantId: string) => {
    const { accessToken } = await platformApi.enterTenant(tenantId);
    const entered = await api.adoptTenantSession(accessToken);
    queryClient.clear();
    setUser(entered);
    return entered;
  }, [queryClient]);

  const leaveTenant = useCallback(() => {
    api.leaveTenant();
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  const value = useMemo(
    () => ({ user, isLoading, sessionLost, login, startDemo, logout, enterTenant, leaveTenant }),
    [user, isLoading, sessionLost, login, startDemo, logout, enterTenant, leaveTenant],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
